'use strict';

/**
 * Examples:
 *
 * <img src="${lowresImageUrl}" data-lazy-src="${imageUrl}">
 *
 * <div style="background-image:url('${lowresImageUrl}')" data-lazy-bg-image="${imageUrl}"></div>
 *
 * <video autoplay loop muted playsinline data-lazy-play>
 *   <source data-lazy-src="${videoUrl}" type="video/mp4">
 * </video>
 *
 * <picture>
 *   <source srcset="${lowresWebPImageUrl}" data-lazy-src="${imageWebPUrl}" type="image/webp">
 *   <source srcset="${lowresImageUrl}" data-lazy-src="${imageUrl}" type="image/jpeg">
 *   <img src="${lowresImageUrl}" data-lazy-src="${imageUrl}">
 * </picture>
 *
 * Notes:
 *
 * Element src and style background-image are optional but they can be used to view low resolution/placeholder image before the laze loaded image is loaded.
 * Video element could have data-lazy-play attribute when the video will play/pause automatically when it is on/off screen. data-lazy-play attribute will have a value 'done' right after the video starts playing at the first time.
 */

const OBSERVER_SETTINGS = {
  rootMargin: '100px 0px 100px 0px', // -200px 0px -200px 0px
  threshold: 0
};

const loadLazyMedia = function(el){
  if(el.tagName === 'VIDEO'){
    const sourceEls = el.querySelectorAll('source[data-lazy-src]');
    sourceEls.forEach(sourceEl => {
      if(sourceEl.dataset.lazySrc){
        sourceEl.setAttribute('src', sourceEl.dataset.lazySrc);
      }
    });
    el.load();
  } else if(el.tagName === 'PICTURE'){
    const sourceEls = el.querySelectorAll('source[data-lazy-src]');
    sourceEls.forEach(sourceEl => {
      if(sourceEl.dataset.lazySrc){
        sourceEl.setAttribute('srcset', sourceEl.dataset.lazySrc);
      }
    });
  } else if(el.dataset.lazySrc){
    let preloader = new Image();
    preloader.addEventListener('load', function(evt){
      el.setAttribute('src', evt.target.getAttribute('src'));
    });
    preloader.setAttribute('src', el.dataset.lazySrc);
  } else if(el.dataset.lazyBgImage){
    let preloader = new Image();
    preloader.addEventListener('load', function(evt){
      el.style.backgroundImage = 'url(' + evt.target.getAttribute('src') + ')';
    });
    preloader.setAttribute('src', el.dataset.lazyBgImage);
  }
};

const videoCanPlay = function(evt){
  const el = evt.target;
  el.removeEventListener('canplay', videoCanPlay);
  el.setAttribute('data-lazy-play', 'done');
};

let lazyLoadMedia = {

  init: function(){
    document.querySelectorAll('img[data-lazy-src]').forEach(el => {
      if(el.parentElement.tagName === 'PICTURE'){
        // Based on intersection observer support:
        if('IntersectionObserver' in window){
          // Remove data-lazy-src from IMG tag inside picture because we lazy load from source tag (prevent both to be loaded).
          el.removeAttribute('data-lazy-src');
        } else {
          el.parentElement.querySelectorAll('source[data-lazy-src]').forEach(sourceEl => {
            // Remove data-lazy-src from SOURCE tags inside picture so only lazy src from img will be used (img is the fallback)
            sourceEl.removeAttribute('data-lazy-src');
          });
        }
      }
    });

    const lazyLoadEls = document.querySelectorAll('[data-lazy-bg-image],[data-lazy-src]');
    const lazyPlayEls = document.querySelectorAll('video[data-lazy-play]');

    console.log('lazyLoadMedia.init()','Found '+lazyLoadEls.length+' lazy load elements and '+lazyPlayEls.length+' lazy play elements.');

    if('IntersectionObserver' in window){
      // IntersectionObserver is supported

      //
      // Lazy play observer
      //

      const lazyPlayObserver = new IntersectionObserver(function(entries, observer){
        entries.forEach(entry => {
          if(entry.isIntersecting && entry.target.offsetParent !== null) {
            if(entry.target.readyState === 4){
              entry.target.play();
            }
          } else {
            entry.target.pause();
          }
        });
      }, OBSERVER_SETTINGS);

      lazyPlayEls.forEach(el => {
        el.addEventListener('canplay', videoCanPlay);
        lazyPlayObserver.observe(el);
      });

      //
      // Lazy load orserver
      //

      const lazyLoadObserver = new IntersectionObserver(function(entries, observer){
        entries.forEach(entry => {
          if(entry.isIntersecting && entry.target.offsetParent !== null) {
            loadLazyMedia(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, OBSERVER_SETTINGS);

      lazyLoadEls.forEach(el => {
        lazyLoadObserver.observe((el.tagName === 'SOURCE' ? el.parentNode : el));
      });

    } else {
      // IntersectionObserver is NOT supported
      console.log('lazyLoadMedia.init()','IntersectionObserver not supported.');

      // Load all instantly

      lazyPlayEls.forEach(el => {
        el.addEventListener('canplay', videoCanPlay);
      });

      lazyLoadEls.forEach(el => {
        loadLazyMedia((el.tagName === 'SOURCE' ? el.parentNode : el));
      });

    }
  }
};

export default lazyLoadMedia;
